import type {
  ClaimLinkOpenViewDto,
  ClaimLinkStatus,
  ClaimLinkViewDto,
  ExpenseLineItem,
  LinkPickResultDto,
} from '@splitfiesta/shared';
import { formatAmountForCurrency, stakeOf } from '@splitfiesta/shared';

import { expenseItemsPreview } from './claimReconcile';
import { type ClaimFooter, claimFooter } from './picks';

export type ClaimLinkPerson = {
  userId: string;
  name: string;
  state: 'picked' | 'notYet' | 'paid';
  hasApp: boolean;
};

export type LinkLifeLine = { count: number; days: number };

export type ClaimShareMessageArgs = {
  title: string;
  /** Already formatted with its currency. */
  amount: string;
  count: number;
  items: number;
  link: string;
};

/** What the viewer owes beyond their own units pays for. */
export type RestPartKind = ClaimFooter['restKind'];

/** The claimLink locale key for each kind (`claim.footer.*`, `done.rest.*`). */
export const REST_LABEL_KEY: Record<
  RestPartKind,
  'extras' | 'unpicked' | 'both' | 'discount'
> = {
  tipTax: 'extras',
  unpicked: 'unpicked',
  both: 'both',
  discount: 'discount',
};

export type AfterClaimSummary = {
  owes: number;
  currency: string;
  items: number;
  /** `amount` is the viewer's picked units only; their part of the rest is in `rest`. */
  shown: Array<{ item: ExpenseLineItem; amount: number }>;
  more: number;
  /** `owes` less every item amount, negative for a discount; null at 0. */
  rest: { amount: number; kind: RestPartKind } | null;
  picked: number;
  people: number;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const SHOWN_ITEMS = 3;

export function isOpenClaimLink(
  view: ClaimLinkViewDto
): view is ClaimLinkOpenViewDto {
  return view.status === 'open';
}

export function claimLinkPeople(view: ClaimLinkOpenViewDto): ClaimLinkPerson[] {
  return view.people.map(p => ({
    userId: p.userId,
    name: p.name,
    state: p.isPayer ? 'paid' : p.picked ? 'picked' : 'notYet',
    hasApp: p.hasApp,
  }));
}

export function claimLinkStatus(view: ClaimLinkViewDto): ClaimLinkStatus {
  return view.status;
}

/** "Link works until all {{count}} have picked, or {{days}} more days". */
export function linkLifeLine(
  view: ClaimLinkOpenViewDto,
  now: Date
): LinkLifeLine {
  const left = Date.parse(view.expiresAt) - now.getTime();
  return {
    count: view.total,
    days: Math.max(0, Math.ceil(left / DAY_MS)),
  };
}

export function claimShareMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  args: ClaimShareMessageArgs
): string {
  return t('expenses:picks.share.message', args);
}

export function claimLinkUrl(webUrl: string, token: string): string {
  return `${webUrl.replace(/\/$/, '')}/c/${token}`;
}

/** The viewer's part beyond their own units, signed (a discount is negative); null at 0. */
export function footerRest(
  footer: ClaimFooter | null
): { amount: number; kind: RestPartKind } | null {
  return footer && footer.rest !== 0
    ? { amount: footer.rest, kind: footer.restKind }
    : null;
}

/** A discount reads "−C$ 2.00", the way the app writes its discount line. */
export function formatRestAmount(amount: number, currency: string): string {
  const formatted = formatAmountForCurrency(Math.abs(amount), currency);
  return amount < 0 ? `\u2212${formatted}` : formatted;
}

/** The done view, from the bill the pick answered (open or, after the last pick, stopped). */
export function afterClaimSummary(
  view: LinkPickResultDto,
  participantId: string
): AfterClaimSummary {
  const { expense } = view;
  const rows = expense.lineItems;
  const preview = expenseItemsPreview(expense, rows);
  const footer = claimFooter(preview, rows, participantId);
  const held = footer ? rows.filter(i => stakeOf(i, participantId) > 0) : [];
  return {
    owes: footer?.total ?? 0,
    currency: preview.entryCurrency,
    items: held.length,
    shown: held.slice(0, SHOWN_ITEMS).map(item => ({
      item,
      amount: claimFooter(preview, [item], participantId)?.itemsAmount ?? 0,
    })),
    more: Math.max(0, held.length - SHOWN_ITEMS),
    rest: footerRest(footer),
    picked: view.picked,
    people: view.total,
  };
}
